import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Pencil, Package, Building2, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORY_SUGGESTIONS = ["HVAC", "Refrigeration", "Lighting", "Plumbing", "IT/Electronics", "Furniture", "Other"];

export default function AssetsPanel({ outlets, readOnly, canDelete }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outletFilter, setOutletFilter] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [outletId, setOutletId] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [serial, setSerial] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const nonInternalOutlets = outlets.filter(o => !o.is_internal);
  const outletMap = Object.fromEntries(outlets.map(o => [o.id, o]));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assets");
      setAssets(data);
    } catch {
      toast.error("Could not load assets");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = outletFilter === "__all__" ? assets : assets.filter(a => a.outlet_id === outletFilter);

  const openCreate = () => {
    setEditing(null);
    setOutletId(nonInternalOutlets[0]?.id || "");
    setName(""); setCategory(""); setSerial(""); setNotes("");
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setOutletId(a.outlet_id);
    setName(a.name); setCategory(a.category || ""); setSerial(a.serial_number || ""); setNotes(a.notes || "");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!outletId) return toast.error("Please pick an outlet");
    if (name.trim().length < 2) return toast.error("Name must be at least 2 characters");
    setSaving(true);
    try {
      const payload = { outlet_id: outletId, name: name.trim(), category: category.trim(), serial_number: serial.trim(), notes: notes.trim() };
      if (editing) {
        await api.put(`/assets/${editing.id}`, payload);
        toast.success("Asset updated");
      } else {
        await api.post("/assets", payload);
        toast.success("Asset added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!confirm(`Delete asset "${a.name}"? Existing tickets keep the record.`)) return;
    try { await api.delete(`/assets/${a.id}`); toast.success("Asset deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="text-sm font-semibold text-slate-800">
            {filtered.length} asset{filtered.length !== 1 ? "s" : ""}
          </div>
          <Select value={outletFilter} onValueChange={setOutletFilter}>
            <SelectTrigger data-testid="assets-outlet-filter" className="h-9 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All outlets</SelectItem>
              {nonInternalOutlets.map(o => (
                <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!readOnly && (
          <Button data-testid="add-asset-btn" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9">
            <Plus className="w-4 h-4 mr-1.5" /> Add asset
          </Button>
        )}
      </div>
      {loading ? (
        <div className="p-8 grid place-items-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">
          {outletFilter === "__all__" ? "No assets yet — start tracking your fridges, ACs, signage boards…" : "No assets registered for this outlet yet."}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filtered.map((a) => (
            <li key={a.id} data-testid={`asset-row-${a.id}`} className="p-4 flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900 text-sm inline-flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5 text-slate-500" /> {a.name}
                  </span>
                  {a.category && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 text-[10px] font-semibold">
                      {a.category}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{outletMap[a.outlet_id]?.code || "—"} · {outletMap[a.outlet_id]?.name || "—"}</span>
                  {a.serial_number && <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" />{a.serial_number}</span>}
                </div>
                {a.notes && <div className="text-xs text-slate-600 mt-1">{a.notes}</div>}
              </div>
              <div className="flex items-center gap-1.5">
                {!readOnly && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(a)} data-testid={`edit-asset-${a.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                )}
                {canDelete && (
                  <Button variant="ghost" size="sm" onClick={() => remove(a)} data-testid={`del-asset-${a.id}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit asset" : "New asset"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Outlet</Label>
              <Select value={outletId} onValueChange={setOutletId}>
                <SelectTrigger data-testid="asset-outlet" className="mt-1.5 h-11"><SelectValue placeholder="Choose outlet" /></SelectTrigger>
                <SelectContent>
                  {nonInternalOutlets.map(o => <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name</Label>
              <Input data-testid="asset-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. Blue Star split AC – counter" />
            </div>
            <div>
              <Label>Category</Label>
              <Input data-testid="asset-category" list="asset-category-list" value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. HVAC, Refrigeration…" />
              <datalist id="asset-category-list">
                {CATEGORY_SUGGESTIONS.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <Label>Serial / Model number</Label>
              <Input data-testid="asset-serial" value={serial} onChange={(e) => setSerial(e.target.value)} className="mt-1.5 h-11" placeholder="Optional" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea data-testid="asset-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" placeholder="Location, warranty, install date…" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="asset-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Add asset"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
